import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HeaderService } from '@core/services/header.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ApiService } from '@core/services/api.service';
import { CompanySettingsService } from '@core/services/company-settings.service';
import { AuthService } from '@core/services/auth.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { forkJoin } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  activeTab: 'company' | 'brand' | 'security' | 'geolocation' | 'demo' = 'company';
  userRole = 'employee';

  company: any = {
    companyName: '',
    legalName: '',
    logo: '',
    industry: '',
    companySize: '',
    cinNumber: '',
    gstNumber: '',
    panNumber: '',
    website: '',
    supportEmail: '',
    registeredAddress: '',
    phoneNumber: ''
  };

  logoPreview: string | null = null;
  saving = false;
  loading = true;

  notifications = { email: true, leaveApprovals: true, attendanceAlerts: false };
  appearance = { darkMode: false, compactMode: false };
  security = { twoFactor: false, sessionTimeout: false, sessionDuration: 30 };
  
  geolocation: any = {
    enabled: false,
    latitude: 0,
    longitude: 0,
    radius: 100,
    allowManualOverride: false
  };
  geoSaving = false;
  detectingLocation = false;

  zones: any[] = [];
  zonesLoading = false;
  showZoneForm  = false;
  zoneSubmitting = false;
  editingZoneId: string | null = null;
  departments: any[] = [];
  employees:   any[] = [];

  zoneForm: any = {
    name: '', latitude: null, longitude: null, radiusMeters: 200,
    scopeType: 'global', scopeId: null, address: '', enabled: true
  };
  detectingZoneLocation = false;

  constructor(
    private headerService: HeaderService,
    private snackbarService: SnackbarService,
    private apiService: ApiService,
    private companySettingsService: CompanySettingsService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.headerService.setTitle('Settings');
    this.userRole = (this.authService.getUserRole() || 'employee').toLowerCase();
    this.loadCompanySettings();
    this.loadGeolocationSettings();
    this.loadZones();
    this.loadZoneDependencies();
    if (this.canEdit) this.loadDemoStatus();
  }

  get canEdit(): boolean {
    return this.userRole === 'admin' || this.userRole === 'super_admin' || this.userRole === 'role_admin';
  }

  loadCompanySettings(): void {
    this.loading = true;
    this.apiService.get('/company-settings').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data) {
          this.company = { ...this.company, ...data };
          if (data.logo) {
            this.logoPreview = data.logo;
          }
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  onLogoSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      this.snackbarService.showFailure('Logo must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.logoPreview = reader.result as string;
      this.company.logo = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  removeLogo(): void {
    this.logoPreview = null;
    this.company.logo = '';
  }

  saveCompany(): void {
    this.saving = true;
    this.apiService.post('/company-settings', this.company).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved: any) => {
        this.company = { ...this.company, ...saved };
        this.companySettingsService.updateSettings(saved);
        this.snackbarService.showSuccess('Company settings saved successfully');
        this.saving = false;
      },
      error: () => {
        this.snackbarService.showFailure('Failed to save company settings');
        this.saving = false;
      }
    });
  }

  loadGeolocationSettings(): void {
    this.apiService.get('/settings/geolocation').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data) {
          this.geolocation = { ...this.geolocation, ...data };
        }
      },
      error: () => {
      }
    });
  }

  detectCurrentLocation(): void {
    if (!navigator.geolocation) {
      this.snackbarService.showFailure('Geolocation is not supported by your browser');
      return;
    }

    this.detectingLocation = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.geolocation.latitude = parseFloat(position.coords.latitude.toFixed(6));
        this.geolocation.longitude = parseFloat(position.coords.longitude.toFixed(6));
        this.detectingLocation = false;
        this.snackbarService.showSuccess('Location detected successfully');
      },
      (error) => {
        this.detectingLocation = false;
        this.snackbarService.showFailure('Failed to detect location. Please enable location services.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }

  saveGeolocationSettings(): void {
    if (this.geolocation.enabled && (!this.geolocation.latitude || !this.geolocation.longitude)) {
      this.snackbarService.showFailure('Please set a valid location before enabling geofencing');
      return;
    }

    if (this.geolocation.radius < 10 || this.geolocation.radius > 5000) {
      this.snackbarService.showFailure('Radius must be between 10 and 5000 meters');
      return;
    }

    this.geoSaving = true;
    this.apiService.post('/settings/geolocation', this.geolocation).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snackbarService.showSuccess('Geolocation settings saved successfully');
        this.geoSaving = false;
      },
      error: () => {
        this.snackbarService.showFailure('Failed to save geolocation settings');
        this.geoSaving = false;
      }
    });
  }


  loadZones(): void {
    this.zonesLoading = true;
    this.apiService.get('/geofence-zones').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.zones = Array.isArray(data) ? data : (data?.data || []);
        this.zonesLoading = false;
      },
      error: () => { this.zones = []; this.zonesLoading = false; }
    });
  }

  loadZoneDependencies(): void {
    forkJoin({
      depts: this.apiService.get('/departments'),
      emps:  this.apiService.get('/employees')
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (d: any) => {
        this.departments = Array.isArray(d.depts) ? d.depts : (d.depts?.data || []);
        this.employees   = Array.isArray(d.emps)  ? d.emps  : (d.emps?.data  || []);
      },
      error: () => {}
    });
  }

  openZoneForm(zone?: any): void {
    if (zone) {
      this.editingZoneId = zone.id;
      this.zoneForm = { ...zone };
    } else {
      this.editingZoneId = null;
      this.zoneForm = { name: '', latitude: null, longitude: null, radiusMeters: 200,
                        scopeType: 'global', scopeId: null, address: '', enabled: true };
    }
    this.showZoneForm = true;
  }

  closeZoneForm(): void { this.showZoneForm = false; }

  detectZoneLocation(): void {
    if (!navigator.geolocation) { this.snackbarService.showFailure('Geolocation not supported'); return; }
    this.detectingZoneLocation = true;
    navigator.geolocation.getCurrentPosition(
      pos => {
        this.zoneForm.latitude  = parseFloat(pos.coords.latitude.toFixed(6));
        this.zoneForm.longitude = parseFloat(pos.coords.longitude.toFixed(6));
        this.detectingZoneLocation = false;
        this.snackbarService.showSuccess('Location detected');
      },
      () => { this.detectingZoneLocation = false; this.snackbarService.showFailure('Could not detect location'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  submitZone(): void {
    if (!this.zoneForm.name?.trim()) { this.snackbarService.showFailure('Zone name is required'); return; }
    if (!this.zoneForm.latitude || !this.zoneForm.longitude) { this.snackbarService.showFailure('Location is required'); return; }
    if (this.zoneForm.scopeType !== 'global' && !this.zoneForm.scopeId) {
      this.snackbarService.showFailure('Please select the target ' + this.zoneForm.scopeType); return;
    }
    const payload = { ...this.zoneForm,
      scopeId: this.zoneForm.scopeType === 'global' ? null : this.zoneForm.scopeId };
    this.zoneSubmitting = true;
    const req = this.editingZoneId
      ? this.apiService.patch('/geofence-zones/' + this.editingZoneId, payload)
      : this.apiService.post('/geofence-zones', payload);
    req.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snackbarService.showSuccess(this.editingZoneId ? 'Zone updated' : 'Zone created');
        this.zoneSubmitting = false;
        this.showZoneForm = false;
        this.loadZones();
      },
      error: () => { this.snackbarService.showFailure('Failed to save zone'); this.zoneSubmitting = false; }
    });
  }

  deleteZone(id: string): void {
    this.confirmDialog.confirm('Employees assigned to this zone will fall back to the department or global zone.', {
      title: 'Delete Geofence Zone', confirmLabel: 'Delete', danger: true
    }).then(ok => {
      if (!ok) return;
      this.apiService.delete('/geofence-zones/' + id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => { this.snackbarService.showSuccess('Zone deleted'); this.loadZones(); },
        error: () => this.snackbarService.showFailure('Failed to delete zone')
      });
    });
  }

  toggleZone(zone: any): void {
    this.apiService.patch('/geofence-zones/' + zone.id, { ...zone, enabled: !zone.enabled }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { zone.enabled = !zone.enabled; },
      error: () => this.snackbarService.showFailure('Failed to update zone')
    });
  }

  getScopeLabel(zone: any): string {
    if (zone.scopeType === 'global') return 'All Employees';
    if (zone.scopeType === 'department') {
      const d = this.departments.find((d: any) => d.id === zone.scopeId);
      return d ? d.name : 'Department';
    }
    const e = this.employees.find((e: any) => e.id === zone.scopeId);
    return e ? `${e.firstName} ${e.lastName}` : 'Employee';
  }

  empNameFn = (e: any) => e ? `${e.firstName || ''} ${e.lastName || ''}`.trim() : '';

  demoStatus: any = null;
  demoLoading = false;
  demoSeeding = false;
  demoClearing = false;

  loadDemoStatus(): void {
    this.demoLoading = true;
    this.apiService.get('/admin/demo-data/status').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => { this.demoStatus = data; this.demoLoading = false; },
      error: () => { this.demoLoading = false; }
    });
  }

  seedDemoData(): void {
    this.confirmDialog.confirm(
      'This will create demo employees, departments, teams, attendance, and other sample records. Proceed?'
    ).then(confirmed => {
      if (!confirmed) return;
      this.demoSeeding = true;
      this.apiService.post('/admin/demo-data/seed', {}).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.snackbarService.showSuccess('Demo data seeded successfully');
          this.demoSeeding = false;
          this.loadDemoStatus();
        },
        error: () => {
          this.snackbarService.showFailure('Failed to seed demo data');
          this.demoSeeding = false;
        }
      });
    });
  }

  clearDemoData(): void {
    this.confirmDialog.confirm(
      'This will permanently delete all demo employees, departments, attendance records and more. This cannot be undone. Proceed?'
    ).then(confirmed => {
      if (!confirmed) return;
      this.demoClearing = true;
      this.apiService.delete('/admin/demo-data/clear').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.snackbarService.showSuccess('Demo data cleared successfully');
          this.demoClearing = false;
          this.loadDemoStatus();
        },
        error: () => {
          this.snackbarService.showFailure('Failed to clear demo data');
          this.demoClearing = false;
        }
      });
    });
  }
}
